import type { VitalsHomeResponse, PatientInfo } from "@/types/appointments";
import type { User } from "@/types/ping";

/**
 * Vital reading with min/max values
 */
interface VitalReading {
  min?: number | null;
  max?: number | null;
  latest_reading?: string | null;
  systolic_min?: number | null;
  systolic_max?: number | null;
  diastolic_min?: number | null;
  diastolic_max?: number | null;
}

/**
 * Format blood pressure range for display
 * Returns format: "systolic_min-systolic_max, diastolic_min-diastolic_max"
 */
export const formatBloodPressureRange = (bp: VitalsHomeResponse['blood_pressure']): string => {
  if (!bp || bp.systolic_min === null || bp.systolic_max === null ||
      bp.diastolic_min === null || bp.diastolic_max === null) {
    return '--/--';
  }
  return `${bp.systolic_min}-${bp.systolic_max}, ${bp.diastolic_min}-${bp.diastolic_max}`;
};

/**
 * Format a min-max range for display
 * Returns format: "min-max" or "--" if no data
 */
export const formatVitalRange = (min: number | null, max: number | null): string => {
  if (min === null || max === null) return '--';
  return `${min}-${max}`;
};

/**
 * Check if a vital reading has any data
 */
export const hasVitalData = (vital: VitalReading | undefined): boolean => {
  if (!vital) return false;
  if (vital.min !== null || vital.max !== null || vital.latest_reading !== null) return true;
  if (vital.systolic_min !== null || vital.systolic_max !== null ||
      vital.diastolic_min !== null || vital.diastolic_max !== null) return true;
  return false;
};

/**
 * Derive a User object from PatientInfo for use with chart components
 */
export const deriveChartUser = (patientInfo: PatientInfo | undefined): User | null => {
  if (!patientInfo) return null;
  return {
    id: patientInfo.user_id,
    first_name: patientInfo.full_name.split(' ')[0] || '',
    last_name: patientInfo.full_name.split(' ').slice(1).join(' ') || '',
    phone_number: '',
    email: null,
    profile_pic: patientInfo.profile_pic,
    age: parseInt(patientInfo.age) || 0,
    gender: patientInfo.sex,
    zarity_user_id: patientInfo.zarity_id,
    city: patientInfo.location
  };
};



